import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

/** 配送位置情報インポート行 */
export type ShipmentLocationImportViewModel = {
  /** 位置情報名 */
  locationName: string;
  /** 住所 */
  address: string;
  /** 経度 */
  longitude: string;
  /** 緯度 */
  latitude: string;
};

/** ヘッダーのマッピング */
const headerMapping: { [key: string]: string } = {
  'Location Name': 'locationName',
  Address: 'address',
  Longitude: 'longitude',
  Latitude: 'latitude',
};

/** インポート可能な最大データ行数（ヘッダ・空行除く） */
const MAX_IMPORT_ROWS = 100;

@Component({
  selector: 'app-shipment-location-bulk-add-dialog',
  standalone: true,
  templateUrl: './shipment-location-bulk-add-dialog.component.html',
  styleUrl: './shipment-location-bulk-add-dialog.component.scss',
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
  ],
})
export class ShipmentLocationBulkAddDialogComponent {
  /** インポートしたファイル名 */
  public fileName = 'ファイルが選択されていません';

  /** インポートした位置情報一覧 */
  public locationList: ShipmentLocationImportViewModel[] = [];

  /** ファイルフォームコントロール */
  public fileControl: FormControl<string | null> = new FormControl('');

  /** エラーメッセージ */
  public fileError: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<ShipmentLocationBulkAddDialogComponent>,
  ) {}

  /**
   * 選択されたファイルを読み込む
   * CSV以外のファイルはエラー扱いにする
   */
  public onFileSelected(): void {
    const inputNode: HTMLInputElement | null = document.querySelector('#file');
    this.locationList = [];
    this.fileName = 'ファイルが選択されていません';
    this.fileError = null;

    if (typeof FileReader === 'undefined') {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      // ファイル内容をCSVとして解析する
      const csvText = reader.result as string;
      if (csvText !== null) {
        const parsedLocations = this.parseCSV(csvText);
        const importRowCount = this.countImportableRows(parsedLocations);
        if (importRowCount > MAX_IMPORT_ROWS) {
          this.locationList = [];
          this.fileControl.setErrors({
            rowCountExceeded: true,
          });
          return;
        }
        this.fileControl.setErrors(null);
        this.locationList = parsedLocations;
      }
    };

    if (inputNode?.files?.length) {
      const file = inputNode.files[0];
      const selectedFileName = file.name;
      const fileExtension = selectedFileName.split('.').pop()?.toLowerCase();

      if (fileExtension === 'csv') {
        this.fileName = selectedFileName;
        this.fileControl.setErrors(null);
        // 既存のデバイスインポートと合わせてSJISで読み込む
        reader.readAsText(file, 'SJIS');
      } else {
        this.fileError = 'CSVファイルのみを選択してください。';
        this.fileControl.setErrors({
          incorrectFileType: true,
        });
        inputNode.value = '';
      }
    }
  }

  /**
   * CSVテキストを位置情報配列へ変換する
   * @param csvText CSV文字列
   * @returns 位置情報インポート行配列
   */
  private parseCSV(csvText: string): ShipmentLocationImportViewModel[] {
    const lines = csvText
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n');
    if (lines.length === 0) {
      return [];
    }

    const headers = lines[0]
      .split(',')
      .map((header) => this.normalizeCsvValue(header));
    const mappedHeaders = headers.map(
      (header) => headerMapping[header] || header,
    );
    const columnCount = headers.length;

    const modifiedLines: string[] = [];
    let currentLine = '';
    // ダブルクオートで囲まれた複数行データを1行として扱う
    for (const line of lines.slice(1)) {
      if (currentLine === '') {
        currentLine = line;
        continue;
      }

      if ((currentLine.match(/"/g) || []).length % 2 === 0) {
        modifiedLines.push(currentLine);
        currentLine = line;
      } else {
        currentLine += '\n' + line;
      }
    }

    if (currentLine !== '') {
      modifiedLines.push(currentLine);
    }

    // 空行は列数を合わせた空データとして扱う
    const processedLines = modifiedLines.map((line) =>
      line.trim() === '' ? ','.repeat(Math.max(columnCount - 1, 0)) : line,
    );

    return processedLines.map((line) => {
      const values: string[] = [];
      let currentValue = '';
      let insideQuotes = false;

      // ダブルクオート外のカンマのみ区切り文字として扱う
      for (const char of line) {
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          values.push(this.normalizeCsvValue(currentValue));
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(this.normalizeCsvValue(currentValue));

      const location: ShipmentLocationImportViewModel = {
        locationName: '',
        address: '',
        longitude: '',
        latitude: '',
      };

      mappedHeaders.forEach((header, index) => {
        if (header in location) {
          (location as Record<string, unknown>)[header] = values[index] ?? '';
        }
      });

      return location;
    });
  }

  /**
   * CSVのセル値を正規化する
   * @param value CSVセル値
   * @returns 前後空白と引用符を除いた値
   */
  private normalizeCsvValue(value: string): string {
    let normalizedValue = value.trim();
    if (
      normalizedValue.length >= 2 &&
      normalizedValue.startsWith('"') &&
      normalizedValue.endsWith('"')
    ) {
      normalizedValue = normalizedValue.slice(1, -1);
    }
    return normalizedValue.replace(/""/g, '"').trim();
  }

  /**
   * インポート対象として有効な行数を返す
   * 空白のみ行は対象外（ヘッダはparseCSVで除外済み）
   * @param locationList CSVから変換した位置情報一覧
   * @returns インポート対象行数
   */
  private countImportableRows(
    locationList: ShipmentLocationImportViewModel[],
  ): number {
    return locationList.filter((location) =>
      Object.values(location).some((value) => value.trim() !== ''),
    ).length;
  }

  /**
   * インポート対象を親コンポーネントへ返却する
   */
  public bulkAdd(): void {
    this.dialogRef.close(this.locationList);
  }
}
